import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { Spin } from 'antd';
import { FormInstance } from 'antd/es/form';
// Api
import { GET_CUSTOM_CAMPAIGN_DRAFT_OFFERS } from 'api/campaignV2/queries';
// Types
import {
  GetCustomCampaignDraftOffers,
  GetCustomCampaignDraftOffersVariables,
} from 'api/campaignV2/types/GetCustomCampaignDraftOffers';
// Utils
import { PlatformCriteriaItem } from 'helpers/customCampaignConstants';
// Components
import TalentAudienceSection from '../TalentAudienceSection/TalentAudienceSection';
import PreselectedMultiTalentSection from '../PreselectedMultiTalentSection/PreselectedMultiTalentSection';
// Style
import styles from './CustomCampaignTalentStep.module.scss';

const EMPTY_OFFERS: GetCustomCampaignDraftOffers['getCampaignOffers']['entities'] =
  [];

type CustomCampaignTalentStepProps = {
  form: FormInstance;
  isPrivate: boolean;
  /** Persisted Campaign.isPrivate — decides roster vs filters independently of form state. */
  persistedIsPrivate: boolean;
  onVisibilityChange: (value: boolean) => void;
  platformCriteria: PlatformCriteriaItem[];
  setPlatformCriteria: React.Dispatch<
    React.SetStateAction<PlatformCriteriaItem[]>
  >;
  campaignId: string;
  ownerId?: string;
  canEditPremiumFilters: boolean;
  onTalentIdsChange: (talentIds: string[]) => void;
  onPersistTalentIds: (talentIds: string[]) => Promise<boolean>;
};

/**
 * Custom campaign step 1 (parity with millions-fe CustomCampaignStepOne):
 * - Private campaign with draft offers → preselected talent list (add/remove)
 * - Otherwise → classic talent/audience filters
 *
 * Public campaigns also carry search-generated offers once submitted for review,
 * so offers alone must not switch the view — only the persisted private flag does.
 */
const CustomCampaignTalentStep = ({
  form,
  isPrivate,
  persistedIsPrivate,
  onVisibilityChange,
  platformCriteria,
  setPlatformCriteria,
  campaignId,
  ownerId,
  canEditPremiumFilters,
  onTalentIdsChange,
  onPersistTalentIds,
}: CustomCampaignTalentStepProps): JSX.Element => {
  const { data, loading, refetch } = useQuery<
    GetCustomCampaignDraftOffers,
    GetCustomCampaignDraftOffersVariables
  >(GET_CUSTOM_CAMPAIGN_DRAFT_OFFERS, {
    variables: {
      input: {
        campaignId,
        limit: 50,
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: !campaignId || !persistedIsPrivate,
  });

  const offers = data?.getCampaignOffers?.entities ?? EMPTY_OFFERS;
  const hasPreselectedOffers = persistedIsPrivate && offers.length > 0;

  useEffect(() => {
    if (!hasPreselectedOffers) {
      onTalentIdsChange([]);
    }
  }, [hasPreselectedOffers, onTalentIdsChange]);

  if (campaignId && persistedIsPrivate && loading && !hasPreselectedOffers) {
    return (
      <div className={styles.loader}>
        <Spin />
      </div>
    );
  }

  if (hasPreselectedOffers) {
    return (
      <PreselectedMultiTalentSection
        offers={offers}
        loading={loading}
        refetch={refetch}
        onTalentIdsChange={onTalentIdsChange}
        onPersistTalentIds={onPersistTalentIds}
      />
    );
  }

  return (
    <TalentAudienceSection
      form={form}
      isPrivate={isPrivate}
      onVisibilityChange={onVisibilityChange}
      platformCriteria={platformCriteria}
      setPlatformCriteria={setPlatformCriteria}
      campaignId={campaignId}
      ownerId={ownerId}
      canEditPremiumFilters={canEditPremiumFilters}
    />
  );
};

export default CustomCampaignTalentStep;
